// @ts-check

/**
 * @typedef {import('types/api-responses').ResultCompound} ResultCompound
 */

/**
 * A single cell in the heat-map grid, ready for rendering.
 *
 * @typedef {Object} HeatTableCell
 * @property {number} percent - raw response percentage (0–100)
 * @property {boolean} answered - true when the current user selected this option
 * @property {number} intensity - normalized heat opacity (0–1); 1.0 = highest percent in the result
 */

/**
 * A single row in the heat-map grid.
 *
 * @typedef {Object} HeatTableRow
 * @property {string} label - row header text
 * @property {boolean} isOptOut - true when this row represents an opt-out label
 * @property {HeatTableCell[]} cells - one cell per column, in column order
 */

/**
 * Fully prepared data for rendering a HeatTable.
 *
 * @typedef {Object} HeatTableData
 * @property {string[]} colHeaders - column header texts, one per item
 * @property {HeatTableRow[]} rows - one row per label, in label order
 */

/**
 * Transforms a compound answer result into the shape consumed by HeatTable.
 *
 * Items become column headers and labels become rows. Cell intensity is
 * normalized (0–1) against the maximum percent across the entire result so
 * the hottest cell always renders at full opacity.
 *
 * @param {ResultCompound} result
 * @returns {HeatTableData}
 */
const mapCompoundResultToHeatTable = (result) => {
  const { items, labels } = result;

  const maxPercent = Math.max(0, ...items.flatMap((item) => item.options.map((o) => o.percent)));

  return {
    colHeaders: items.map((item) => item.text),
    rows: labels.map((label, labelIndex) => ({
      label: label.text,
      isOptOut: label.isOptOut ?? false,
      cells: items.map((item) => {
        const option = item.options[labelIndex];
        return {
          percent: option?.percent ?? 0,
          answered: option?.answered ?? false,
          intensity: maxPercent > 0 && option != null ? option.percent / maxPercent : 0,
        };
      }),
    })),
  };
};

export default mapCompoundResultToHeatTable;
